import Vue from 'vue';
// 注册自定义指令
Vue.directive('editable', {
  bind(el, binding, vnode) {
    el.addEventListener('input', () => {
      vnode.context[binding.expression] = el.innerHTML;
    });
  },
  update(el, binding) {
    if (binding.value !== el.innerText) {
      el.innerHTML = binding.value;
    }
  },
});
//图片加载失败加载默认图片
Vue.directive('img-fallback', {
  inserted(el, binding) {
    const defaultImage = binding.value;
    const handleError = () => {
      // 替换为默认图片并移除监听器，防止循环报错
      el.src = defaultImage;
      el.removeEventListener('error', handleError);
    };
    // 存储处理函数以便后续更新或解绑
    el._imgFallbackHandler = handleError;
    // 绑定 error 事件
    el.addEventListener('error', handleError);
  },
  update(el, binding) {
    // 当默认图片地址变化时更新处理逻辑
    if (binding.oldValue !== binding.value) {
      const newDefault = binding.value;
      const newHandler = () => {
        el.src = newDefault;
        el.removeEventListener('error', newHandler);
      };
      // 移除旧的监听器
      if (el._imgFallbackHandler) {
        el.removeEventListener('error', el._imgFallbackHandler);
      }
      // 绑定新的监听器
      el._imgFallbackHandler = newHandler;
      el.addEventListener('error', newHandler);
    }
  },
  unbind(el) {
    // 清理事件监听器
    if (el._imgFallbackHandler) {
      el.removeEventListener('error', el._imgFallbackHandler);
      delete el._imgFallbackHandler;
    }
  },
});

// 工具函数：获取元素计算样式
const getStyle = (el, prop) => window.getComputedStyle(el, null).getPropertyValue(prop);

// 创建 Tooltip 元素
const createTooltip = () => {
  const tooltip = document.createElement('div');
  tooltip.style.position = 'absolute';
  tooltip.style.visibility = 'hidden';
  tooltip.style.pointerEvents = 'none';
  tooltip.style.zIndex = '9999';
  tooltip.classList.add('custom-tooltip');
  return tooltip;
};

// 主指令逻辑
Vue.directive('ellipsis-tooltip', {
  inserted(el, binding) {
    const content = binding.value ?? '';
    let isOverflow = false;
    let tooltip = null;
    const checkOverflow = () => {
      const range = document.createRange();
      range.setStart(el, 0);
      range.setEnd(el, el.childNodes.length);
      const textWidth = range.getBoundingClientRect().width;
      isOverflow = textWidth > el.clientWidth;
      return isOverflow;
    };
    // 更新 Tooltip 位置
    const updatePosition = () => {
      if (!tooltip || !isOverflow) return;

      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      tooltip.style.top = `${rect.top + scrollTop - tooltip.offsetHeight - 6}px`;
      tooltip.style.left = `${rect.left + scrollLeft + (rect.width - tooltip.offsetWidth) / 2}px`;
    };
    // 显示 Tooltip
    const showTooltip = () => {
      if (!isOverflow) return;
      if (!tooltip) {
        tooltip = createTooltip();
        document.body.appendChild(tooltip);
      }
      tooltip.textContent = content;
      tooltip.style.visibility = 'visible';
      updatePosition();
    };
    // 隐藏 Tooltip
    const hideTooltip = () => {
      if (tooltip) {
        tooltip.style.visibility = 'hidden';
      }
    };
    // 事件处理器
    const handleMouseEnter = () => {
      checkOverflow();
      showTooltip();
    };
    const handleMouseLeave = hideTooltip;
    const handleScroll = () => {
      updatePosition();
      hideTooltip();
    };
    // 初始化检测
    const initCheck = () => {
      // 确保元素应用了必要的截断样式
      if (
        getStyle(el, 'white-space') !== 'nowrap' ||
        getStyle(el, 'overflow') !== 'hidden' ||
        !['clip', 'ellipsis'].includes(getStyle(el, 'text-overflow'))
      ) {
        console.warn(
          'v-ellipsis-tooltip 需要配合以下样式使用：white-space: nowrap; overflow: hidden; text-overflow: ellipsis'
        );
      }

      checkOverflow();
    };
    // 绑定事件
    el._ellipsisTooltipHandler = {
      mouseenter: handleMouseEnter,
      mouseleave: handleMouseLeave,
      scroll: handleScroll,
    };
    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, true);
    // 监听窗口变化
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
      if (isOverflow) updatePosition();
    });
    resizeObserver.observe(el);
    // 保存引用用于清理
    el._ellipsisTooltip = {
      resizeObserver,
      tooltip,
      checkOverflow,
      updatePosition,
    };

    // 初始检查
    Vue.nextTick(initCheck);
  },
  update(el, binding) {
    // 当内容变化时重新检测
    if (binding.value?.content !== binding.oldValue?.content) {
      el._ellipsisTooltip?.checkOverflow?.();
    }
  },
  unbind(el) {
    // 清理事件监听
    const handlers = el._ellipsisTooltipHandler;
    if (handlers) {
      el.removeEventListener('mouseenter', handlers.mouseenter);
      el.removeEventListener('mouseleave', handlers.mouseleave);
      window.removeEventListener('scroll', handlers.scroll);
    }

    // 清理 ResizeObserver
    el._ellipsisTooltip?.resizeObserver?.disconnect();

    // 移除 Tooltip
    if (el._ellipsisTooltip?.tooltip) {
      document.body.removeChild(el._ellipsisTooltip.tooltip);
    }

    // 删除引用
    delete el._ellipsisTooltip;
    delete el._ellipsisTooltipHandler;
  },
});

//如果没有子元素展示数据为空提示
Vue.directive('empty-tip', {
  inserted(el, binding) {
    // 获取配置参数
    const config = binding.value || {};
    const tipText = config.text || '暂无数据';
    const tipClass = config.class || 'empty-tip';
    const tipStyle = config.style || {};
    // 创建提示元素
    const tip = document.createElement('div');
    tipClass && tip.classList.add(tipClass);
    tip.textContent = tipText;
    Object.assign(tip.style, tipStyle);
    //判断是否有子元素
    const checkChildren = () => {
      // 检查是否有实际内容的子元素（排除提示元素本身）
      const hasRealChildren = Array.from(el.children).some(
        (child) => !child.classList.contains(tipClass)
      );
      // 检查提示元素是否已经是父元素的子元素
      const tipIsChild = Array.from(el.children).includes(tip);
      if (!hasRealChildren) {
        // 如果没有实际内容的子元素且提示元素不是子元素，则添加提示
        if (!tipIsChild) {
          el.appendChild(tip);
        }
      } else {
        // 如果有实际内容的子元素且提示元素是子元素，则移除提示
        if (tipIsChild) {
          el.removeChild(tip);
        }
      }
    };
    checkChildren();
    el._tipDom = tip;
    el._checkChildren = checkChildren;
  },

  update(el, binding) {
    console.log('update');
    el._checkChildren();
  },

  unbind(el) {
    // 在解绑前检查提示元素是否是子元素
    const tipIsChild = Array.from(el.children).includes(el._tipDom);
    if (tipIsChild) {
      el.removeChild(el._tipDom);
    }
    delete el._tipDom;
    delete el._checkChildren;
  },
});
